import type { Database } from '@/types/supabase'
import { createClient } from '@supabase/supabase-js'
import { upgradeEvents } from '@/lib/upgrade'
import { showCreditRefusal } from '@/lib/upgrade-toast'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
// Prefer new publishable key name (from Supabase-Vercel integration), fallback to legacy anon key
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable')
}

if (!supabaseAnonKey) {
  throw new Error(
    'Missing VITE_SUPABASE_PUBLISHABLE_KEY or VITE_SUPABASE_ANON_KEY environment variable'
  )
}

/** The connection details, for the rare request the client cannot make itself (a keepalive flush). */
export const supabaseConnection = { url: supabaseUrl, anonKey: supabaseAnonKey }

/**
 * The edge functions that spend credits: each runs the credit check before it
 * starts work and answers 402 when the account cannot pay.
 */
const SPENDING_FUNCTIONS = new Set([
  'creation-generate',
  'generation-retry',
  'model-edit',
  'model-generate',
  'scene-edit',
  'scene-generate',
  'stp-edit',
  'stp-generate',
  'studio-generate',
  'upscale-generate',
  'video-generate',
  'vto-edit',
  'vto-generate',
])

function spendingFunction(input: RequestInfo | URL): boolean {
  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url
  const name = /\/functions\/v1\/([^/?#]+)/.exec(url)?.[1]
  return !!name && SPENDING_FUNCTIONS.has(name)
}

/**
 * One place for "you are out of credits", so every action that spends them
 * behaves the same. With nothing left, the call is answered here at once,
 * with the 402 the server would send, instead of after a round trip; and any
 * 402 from the server (a price above what is left) opens the same dialog.
 * The server's check stays the authority either way.
 */
const billingAwareFetch: typeof fetch = async (input, init) => {
  if (!spendingFunction(input)) {
    return fetch(input, init)
  }
  if (upgradeEvents.outOfCredits()) {
    showCreditRefusal()
    return Response.json(
      { error: 'Out of credits', code: 'out_of_credits' },
      { status: 402 }
    )
  }
  const response = await fetch(input, init)
  if (response.status === 402) {
    showCreditRefusal()
  }
  return response
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  global: { fetch: billingAwareFetch },
  auth: {
    flowType: 'pkce',
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
})
